import React, {useEffect, useMemo, useState, Suspense, lazy} from "react";
import {Routes, Route, useLocation} from "react-router-dom";
import Header from "../components/header/Header";
import ChatWidget from "../components/chatbot/ChatWidget";
import MaintenanceBanner from "@shared/ui/MaintenanceBanner";
import ScrollToHash from "@app/ScrollToHash";
import ScrollToTopButton from "./topbutton/Top";
import ErrorBoundary from "@app/ErrorBoundary";
import {splashScreen} from "@features/portfolio/config";
import {StyleProvider} from "../contexts/StyleContext";
import {useSettings} from "../contexts/SettingsContext";
import {usePortfolio} from "../contexts/PortfolioContext";
import {useLocalStorage} from "@shared/hooks/useLocalStorage";
import {trackPageView} from "@shared/lib/analytics";
import Loader from "../components/loader/Loader";
import Seo from "../components/seo/Seo";
import "./Main.scss";

// ---- Route-level code splitting ----
// Each entry below is its own webpack chunk. Public visitors no longer
// download the admin/editor or markdown/syntax-highlighter bundles up front.
const BlogList       = lazy(() => import("./blog/BlogList"));
const BlogDetail     = lazy(() => import("./blog/BlogDetail"));
const Resume         = lazy(() => import("./resume/Resume"));
const AdminLogin     = lazy(() => import("./admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./admin/AdminDashboard"));
const SplashScreen   = lazy(() => import("./splashScreen/SplashScreen"));

// Portfolio composes several heavy sections. Each section is itself lazy so
// the /portfolio chunk doesn't balloon to include every container.
const Greeting        = lazy(() => import("./greeting/Greeting"));
const Impact          = lazy(() => import("./impact/Impact"));
const Skills          = lazy(() => import("./skills/Skills"));
const CoreExpertise   = lazy(() => import("./coreExpertise/CoreExpertise"));
const StackProgress   = lazy(() => import("./skillProgress/skillProgress"));
const WorkExperience  = lazy(() => import("./workExperience/WorkExperience"));
const Education       = lazy(() => import("./education/Education"));
const Profile         = lazy(() => import("./profile/Profile"));
const StartupProject  = lazy(() => import("./StartupProjects/StartupProject"));
const FeaturedProject = lazy(() => import("./featured/FeaturedProject"));
const Certifications  = lazy(() => import("./certifications/Certifications"));
const Testimonials    = lazy(() => import("./testimonials/Testimonials"));

const Portfolio = () => {
  const {isVisible, loaded} = useSettings();
  const {greeting, contact, social, seo} = usePortfolio();
  if (!loaded) return <Loader label="Rashid Ahmad" />;

  // JSON-LD Person schema — makes the site look polished when shared and helps
  // search engines understand who this is. Built from the live portfolio data.
  const name = greeting?.username || "Rashid Ahmad";
  const sameAs = [social?.github, social?.linkedin, social?.gitlab, social?.medium, social?.youtube]
    .filter(Boolean);
  const personLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name,
    jobTitle: "Salesforce / AI Technical Lead",
    email: contact?.email_address ? `mailto:${contact.email_address}` : undefined,
    telephone: contact?.number || undefined,
    url: typeof window !== "undefined" ? window.location.origin : undefined,
    sameAs: sameAs.length ? sameAs : undefined,
  };

  return (
    <>
      <Seo
        title={seo?.title || `${name} — Salesforce Developer & AI Technical Lead`}
        description={
          seo?.description ||
          "Salesforce Developer and AI Technical Lead specialising in Apex, LWC, OmniStudio, integrations and Salesforce + AI (Agentforce / Gen AI) solutions."
        }
        image={seo?.image || undefined}
        type="website"
        jsonLd={personLd}
      />
      {isVisible("sections.greeting")   && <Greeting />}
      <Impact />
      <FeaturedProject />
      {isVisible("sections.skills")     && <Skills />}
      {isVisible("sections.skills")     && <CoreExpertise />}
      {isVisible("sections.techStack")  && <StackProgress />}
      {isVisible("sections.education")  && <Education />}
      {isVisible("sections.experience") && <WorkExperience />}
      {isVisible("sections.projects")   && <StartupProject />}
      <Certifications />
      <Testimonials />
      {isVisible("sections.contact")    && <Profile />}
    </>
  );
};

const RouteFallback = () => <Loader label="Loading" />;

// Per-route ErrorBoundary + Suspense wrapper. Isolating each route means a
// crash on /admin can never blank out / or /blog, and a slow chunk only stalls
// its own subtree.
const RouteShell = ({children}) => (
  <ErrorBoundary>
    <Suspense fallback={<RouteFallback />}>{children}</Suspense>
  </ErrorBoundary>
);

const Main = () => {
  const darkPref = window.matchMedia("(prefers-color-scheme: dark)");
  const [isDark, setIsDark] = useLocalStorage("isDark", darkPref.matches);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Fire-and-forget device/page analytics on every route change — never
  // tracks admin routes (no reason to log the owner's own visits).
  useEffect(() => {
    if (!isAdminRoute) trackPageView(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const [isShowingSplashAnimation, setIsShowingSplashAnimation] =
    useState(true);

  useEffect(() => {
    if (splashScreen.enabled) {
      const splashTimer = setTimeout(
        () => setIsShowingSplashAnimation(false),
        splashScreen.duration
      );
      return () => {
        clearTimeout(splashTimer);
      };
    }
  }, []);

  // Stable identity for the StyleContext value — without useMemo, every Main
  // re-render produces a brand-new {isDark, changeTheme} object and every
  // consumer re-renders even when isDark is unchanged.
  const styleValue = useMemo(
    () => ({isDark, changeTheme: () => setIsDark(d => !d)}),
    [isDark, setIsDark]
  );

  return (
    <div className={isDark ? "dark-mode" : null}>
      <StyleProvider value={styleValue}>
        {isShowingSplashAnimation && splashScreen.enabled ? (
          <Suspense fallback={<RouteFallback />}>
            <SplashScreen />
          </Suspense>
        ) : (
          <>
            {/* Rendered first, in normal document flow (not fixed) — it
                pushes the header down instead of overlaying it, so there's
                no z-index fight or overlap regardless of scroll position. */}
            <MaintenanceBanner />
            <Header />
            <ScrollToHash />
            <Routes>
              <Route path="/"             element={<RouteShell><BlogList /></RouteShell>} />
              <Route path="/blog"         element={<RouteShell><BlogList /></RouteShell>} />
              <Route path="/blog/:slug"   element={<RouteShell><BlogDetail /></RouteShell>} />
              <Route path="/portfolio"    element={<RouteShell><Portfolio /></RouteShell>} />
              <Route path="/resume"       element={<RouteShell><Resume /></RouteShell>} />
              <Route path="/admin/login"  element={<RouteShell><AdminLogin /></RouteShell>} />
              <Route path="/admin"        element={<RouteShell><AdminDashboard /></RouteShell>} />
            </Routes>
            <ScrollToTopButton />
            {!isAdminRoute && <ChatWidget />}
          </>
        )}
      </StyleProvider>
    </div>
  );
};

export default Main;
